import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Download, Inbox, Lock, KeyRound, MailQuestion } from "lucide-react";
import { Logo } from "../components/Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "closed"];
const STATUS_STYLES = {
  new: "border-dg-cyan/40 bg-dg-cyan/10 text-dg-cyan",
  contacted: "border-amber-400/40 bg-amber-400/10 text-amber-300",
  closed: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
};

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-sm text-white placeholder-slate-500 outline-none transition-all duration-300 focus:border-dg-cyan/60";

const errText = (err, fallback) => {
  const d = err.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

const GoogleIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28v-3.1H1.29a12 12 0 0 0 0 10.76l3.98-3.1z" />
    <path fill="#EA4335" d="M12 4.77c1.76 0 3.34.61 4.59 1.8l3.44-3.44A11.97 11.97 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.1C6.22 6.88 8.87 4.77 12 4.77z" />
  </svg>
);

function GoogleCallback({ onSuccess, onFail }) {
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sessionId = window.location.hash.split("session_id=")[1]?.split("&")[0];
    const cleanUrl = () => window.history.replaceState(null, "", window.location.pathname);
    if (!sessionId) {
      cleanUrl();
      onFail();
      return;
    }
    axios
      .post(`${API}/auth/google-session`, { session_id: sessionId })
      .then(({ data }) => {
        localStorage.setItem("dg_token", data.access_token);
        cleanUrl();
        onSuccess(data.access_token);
      })
      .catch(() => {
        cleanUrl();
        onFail();
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="grid min-h-screen place-items-center bg-dg-navy bg-grid-dark noise" data-testid="google-callback-screen">
      <div className="flex flex-col items-center gap-5">
        <Logo />
        <Loader2 size={26} className="animate-spin text-dg-cyan" />
        <p className="text-sm text-slate-400">Signing you in with Google...</p>
      </div>
    </div>
  );
}

function Login({ onLogin, googleError }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const { data } = await axios.post(`${API}/auth/login`, { email, password });
        localStorage.setItem("dg_token", data.access_token);
        onLogin(data.access_token);
        toast.success(`Welcome back, ${data.user.name}`);
      } else {
        const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
        toast.success(data.message);
        setMode("login");
      }
    } catch (err) {
      toast.error(errText(err, mode === "login" ? "Login failed" : "Could not send reset link"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-dg-navy bg-grid-dark px-5 noise">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.03] p-9 backdrop-blur"
      >
        <div className="flex justify-center"><Logo /></div>
        <h1 className="font-display mt-8 text-center text-2xl font-extrabold text-white">
          {mode === "login" ? "Admin Login" : "Reset Password"}
        </h1>
        <p className="mt-1.5 text-center text-sm text-slate-400">
          {mode === "login"
            ? "Enquiries dashboard — authorized access only"
            : "Reset link aapke registered recovery email par jayega"}
        </p>

        {googleError && (
          <p data-testid="google-error" className="mt-5 rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-center text-xs text-red-300">
            {googleError}
          </p>
        )}

        {mode === "login" && (
          <>
            <button
              type="button"
              data-testid="google-signin-btn"
              onClick={googleSignIn}
              className="mt-7 flex w-full items-center justify-center gap-3 rounded-full bg-white px-8 py-3.5 text-sm font-bold text-dg-navy transition-all duration-300 hover:shadow-[0_0_25px_rgba(255,255,255,0.25)]"
            >
              <GoogleIcon /> Sign in with Google
            </button>
            <div className="my-6 flex items-center gap-4">
              <span className="h-px flex-1 bg-white/10" />
              <span className="text-[11px] uppercase tracking-widest text-slate-500">or with email</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>
          </>
        )}

        <div className={`space-y-4 ${mode === "login" ? "" : "mt-8"}`}>
          <input data-testid="admin-email" type="email" required placeholder="Admin Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          {mode === "login" && (
            <input data-testid="admin-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
          )}
        </div>
        <button
          data-testid="admin-login-btn"
          type="submit"
          disabled={loading}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-dg-cyan px-8 py-4 font-bold text-dg-navy transition-all duration-300 hover:bg-white disabled:opacity-60"
        >
          {loading ? <Loader2 size={17} className="animate-spin" /> : mode === "login" ? <Lock size={16} /> : <MailQuestion size={16} />}
          {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Send Reset Link"}
        </button>
        <button
          type="button"
          data-testid="toggle-forgot-btn"
          onClick={() => setMode(mode === "login" ? "forgot" : "login")}
          className="mt-5 w-full text-center text-xs font-semibold text-slate-400 transition-colors hover:text-dg-cyan"
        >
          {mode === "login" ? "Forgot password?" : "Back to login"}
        </button>
      </form>
    </div>
  );
}

export function AdminReset() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, { token, password });
      toast.success(data.message + " — please sign in");
      navigate("/admin");
    } catch (err) {
      toast.error(errText(err, "Reset failed — link may be expired"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-dg-navy bg-grid-dark px-5 noise">
      <form
        onSubmit={submit}
        data-testid="admin-reset-form"
        className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.03] p-9 backdrop-blur"
      >
        <div className="flex justify-center"><Logo /></div>
        <h1 className="font-display mt-8 text-center text-2xl font-extrabold text-white">Set New Password</h1>
        <p className="mt-1.5 text-center text-sm text-slate-400">Minimum 8 characters</p>
        {!token && <p className="mt-4 text-center text-xs text-red-400">Reset token missing — use the link from your email.</p>}
        <div className="mt-8 space-y-4">
          <input data-testid="reset-password" type="password" required minLength={8} placeholder="New Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
          <input data-testid="reset-confirm" type="password" required minLength={8} placeholder="Confirm New Password" value={confirm} onChange={setConfirm} className={inputCls} />
        </div>
        <button
          data-testid="reset-submit-btn"
          type="submit"
          disabled={loading || !token}
          className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-dg-cyan px-8 py-4 font-bold text-dg-navy transition-all duration-300 hover:bg-white disabled:opacity-60"
        >
          {loading ? <Loader2 size={17} className="animate-spin" /> : <KeyRound size={16} />}
          {loading ? "Updating..." : "Update Password"}
        </button>
        <Link to="/admin" data-testid="reset-back-link" className="mt-5 block text-center text-xs font-semibold text-slate-400 transition-colors hover:text-dg-cyan">
          Back to login
        </Link>
      </form>
    </div>
  );
}

function BlogManager({ token }) {
  const [posts, setPosts] = useState(null);
  const [form, setForm] = useState({ title: "", tags: "", excerpt: "", content: "" });
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    const { data } = await axios.get(`${API}/blog`);
    setPosts(data);
  };
  useEffect(() => { load().catch(() => setPosts([])); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const publish = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/blog`, {
        title: form.title,
        excerpt: form.excerpt,
        content: form.content,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      }, { headers });
      toast.success("Article published");
      setForm({ title: "", tags: "", excerpt: "", content: "" });
      load();
    } catch (err) {
      toast.error(errText(err, "Publish failed"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/blog/${id}`, { headers });
      setPosts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Article deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_1.2fr]" data-testid="blog-manager">
      <form onSubmit={publish} className="h-fit rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="blog-create-form">
        <h2 className="font-display text-lg font-bold text-white">New Article</h2>
        <div className="mt-5 space-y-4">
          <input data-testid="blog-title" required minLength={5} placeholder="Title *" value={form.title} onChange={set("title")} className={inputCls} />
          <input data-testid="blog-tags" placeholder="Tags (comma separated)" value={form.tags} onChange={set("tags")} className={inputCls} />
          <textarea data-testid="blog-excerpt" required minLength={10} rows={2} placeholder="Short excerpt (shown on cards & Google) *" value={form.excerpt} onChange={set("excerpt")} className={`${inputCls} resize-none`} />
          <textarea data-testid="blog-content" required minLength={50} rows={10} placeholder={"Article content * (blank line = new paragraph, '## ' = heading, '- ' = bullet)"} value={form.content} onChange={set("content")} className={`${inputCls} resize-y font-mono text-xs`} />
        </div>
        <button data-testid="blog-publish-btn" type="submit" disabled={saving} className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-dg-cyan px-6 py-3 text-sm font-bold text-dg-navy transition-colors hover:bg-white disabled:opacity-60">
          {saving ? <Loader2 size={15} className="animate-spin" /> : null}
          {saving ? "Publishing..." : "Publish Article"}
        </button>
      </form>

      <div className="space-y-3">
        {posts === null ? (
          <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-dg-cyan" /></div>
        ) : posts.length === 0 ? (
          <p className="py-16 text-center text-sm text-slate-500">No articles yet.</p>
        ) : (
          posts.map((p) => (
            <div key={p.id} data-testid={`blog-row-${p.slug}`} className="flex items-center justify-between gap-4 rounded-2xl border border-white/8 bg-white/[0.03] p-5">
              <div className="min-w-0">
                <p className="truncate font-display text-sm font-bold text-white">{p.title}</p>
                <p className="mt-1 text-xs text-slate-500">/blog/{p.slug} · {p.read_time} min read</p>
              </div>
              <button data-testid={`blog-delete-${p.slug}`} onClick={() => remove(p.id)} className="shrink-0 rounded-full border border-white/15 px-4 py-1.5 text-xs font-bold text-slate-400 transition-colors hover:border-red-400 hover:text-red-400">
                Delete
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [enquiries, setEnquiries] = useState(null);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("enquiries");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/enquiries`, { headers: { Authorization: `Bearer ${token}` } });
      setEnquiries(data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Could not load enquiries");
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/enquiries/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Status update failed");
    }
  };

  const exportCsv = () => {
    const rows = [["Name", "Company", "Email", "Phone", "Service", "Budget", "Message", "Status", "Date"]];
    (enquiries || []).forEach((e) =>
      rows.push([e.name, e.company, e.email, e.phone, e.service, e.budget, `"${(e.message || "").replace(/"/g, '""')}"`, e.status, e.created_at])
    );
    const blob = new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "dg-hubs-enquiries.csv";
    a.click();
  };

  const list = (enquiries || []).filter((e) => filter === "all" || e.status === filter);

  return (
    <div className="min-h-screen bg-dg-navy bg-grid-dark noise">
      <header className="sticky top-0 z-30 border-b border-cyan-500/10 bg-dg-navy/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <Logo />
          <div className="flex items-center gap-3">
            <button
              data-testid="export-csv-btn"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-slate-300 transition-colors hover:border-dg-cyan hover:text-dg-cyan"
            >
              <Download size={14} /> Export CSV
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-slate-300 transition-colors hover:border-red-400 hover:text-red-400"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8" data-testid="admin-dashboard">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-white">
              {view === "enquiries" ? "Enquiries" : "Blog Manager"}
            </h1>
            <p className="mt-1 text-sm text-slate-400">
              {view === "enquiries" ? (enquiries ? `${enquiries.length} total` : "Loading...") : "Publish & manage Insights articles"}
            </p>
          </div>
          <div className="flex gap-2">
            {["enquiries", "blog"].map((v) => (
              <button
                key={v}
                data-testid={`view-${v}-tab`}
                onClick={() => setView(v)}
                className={`rounded-full px-4 py-2 text-xs font-bold capitalize transition-all ${
                  view === v ? "bg-dg-cyan text-dg-navy" : "border border-white/15 text-slate-400 hover:text-white"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {view === "blog" && <BlogManager token={token} />}
        {view === "enquiries" && (
        <>
        <div className="mt-6 flex gap-2">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={() => setFilter(s)}
              className={`rounded-full px-4 py-2 text-xs font-bold capitalize transition-all ${
                filter === s ? "bg-dg-blue text-white" : "border border-white/15 text-slate-400 hover:text-white"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {enquiries === null ? (
          <div className="mt-20 flex justify-center"><Loader2 size={28} className="animate-spin text-dg-cyan" /></div>
        ) : list.length === 0 ? (
          <div className="mt-20 flex flex-col items-center gap-3 text-slate-500">
            <Inbox size={36} />
            <p className="text-sm">No enquiries here yet.</p>
          </div>
        ) : (
          <div className="mt-8 grid gap-4">
            {list.map((e) => (
              <div
                key={e.id}
                data-testid={`enquiry-card-${e.id.slice(0, 8)}`}
                className="rounded-2xl border border-white/8 bg-white/[0.03] p-6 transition-colors hover:border-white/15"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-display text-lg font-bold text-white">{e.name}
                      {e.company && <span className="ml-2 text-sm font-normal text-slate-400">· {e.company}</span>}
                    </p>
                    <p className="mt-1 text-sm text-slate-400">{e.email} · {e.phone}</p>
                  </div>
                  <select
                    data-testid={`status-select-${e.id.slice(0, 8)}`}
                    value={e.status}
                    onChange={(ev) => setStatus(e.id, ev.target.value)}
                    className={`rounded-full border px-3.5 py-1.5 text-xs font-bold capitalize outline-none cursor-pointer ${STATUS_STYLES[e.status]} bg-dg-navy [&>option]:bg-dg-navy`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="rounded-full bg-dg-blue/25 px-3 py-1 text-[11px] font-semibold text-dg-cyan">{e.service}</span>
                  {e.budget && <span className="rounded-full bg-white/5 px-3 py-1 text-[11px] text-slate-400">{e.budget}</span>}
                  <span className="rounded-full bg-white/5 px-3 py-1 font-mono text-[10px] text-slate-500">
                    {new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                </div>
                <p className="mt-4 rounded-xl bg-white/[0.03] p-4 text-sm leading-relaxed text-slate-300">{e.message}</p>
              </div>
            ))}
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const location = useLocation();
  const [token, setToken] = useState(() => localStorage.getItem("dg_token"));
  const [googleFlow, setGoogleFlow] = useState(() =>
    location.hash?.includes("session_id=") ? "processing" : "idle"
  );

  const logout = () => {
    localStorage.removeItem("dg_token");
    setToken(null);
  };

  if (googleFlow === "processing") {
    return (
      <GoogleCallback
        onSuccess={(t) => {
          setToken(t);
          setGoogleFlow("done");
        }}
        onFail={() => setGoogleFlow("failed")}
      />
    );
  }
  if (googleFlow === "failed") {
    return <Login onLogin={setToken} googleError="Google sign-in failed, or this Google account is not authorized for admin access." />;
  }
  return token ? <Dashboard token={token} onLogout={logout} /> : <Login onLogin={setToken} />;
}
